import { Link, usePage } from "@inertiajs/react"
import { PlayersIndexProps } from "@/types/types"
import Button from "@/components/Button"
import { PlusIcon, UserIcon } from "@heroicons/react/20/solid"
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import { WP } from "@/components/pieces"
import { Modals, useModals } from "@/hooks/useModals"
import useUser from "@/hooks/useUser"
import SearchInput from "./SearchInput"
import Pagination from "./Pagination"
import SortingSelector from "./SortingSelector"
import { NestedPlayer } from "@/types/serializers"
import Tooltip from "./Tooltip"

const PlayersList = () => {
  const { openModal } = useModals()
  const { user } = useUser()
  const { players, filters } = usePage<PlayersIndexProps>().props
  const search = filters.search || ""

  const hasNoPlayers = !search && players.length === 0

  return (
    <div className="flex flex-1 flex-col pt-6">
      {hasNoPlayers ? (
        <div className="padding mx-auto flex max-w-xs flex-col items-center justify-center py-10">
          <div className="flex items-center justify-center rounded-xl bg-gray-200 p-3">
            <WP className="h-6 w-6 text-gray-500" />
          </div>
          <h3 className="my-4 text-center text-base leading-6 font-semibold text-gray-900">
            Create a player
          </h3>
          <Button size="sm" onClick={() => openModal(Modals.AddPlayer)}>
            <PlusIcon className="h-5 w-5" />
            <span>Add player</span>
          </Button>
        </div>
      ) : (
        <>
          <div className="padding relative flex flex-col items-start justify-start gap-x-8 gap-y-4 sm:flex-row sm:items-center">
            <SearchInput />
            <SortingSelector type="players" />
          </div>

          <div className="relative mt-6 flex flex-1 flex-col">
            {players && players.length > 0 ? (
              <>
                <ul
                  role="list"
                  className="divide-y divide-gray-200 border-y border-gray-200"
                >
                  {players.map((player) => (
                    <PlayerItem
                      key={player.id}
                      isMe={player.id === user?.player_id}
                      {...player}
                    />
                  ))}
                </ul>

                <div className="flex-1" />

                <Pagination type="player" />
              </>
            ) : (
              <div className="padding mx-auto flex max-w-xs flex-col items-center justify-center py-10">
                <div className="flex items-center justify-center rounded-xl bg-gray-200 p-3">
                  <MagnifyingGlassIcon className="h-6 w-6 text-gray-500" />
                </div>
                <h3 className="mt-4 text-center text-base leading-6 font-semibold text-gray-900">
                  No players found.
                </h3>
                <p className="text-center text-sm text-gray-700">
                  &ldquo;{search}&rdquo; did not match the name of any player.
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default PlayersList

const PlayerItem = ({
  id,
  name,
  title,
  elo,
  games_count = 0,
  is_chessdrop_user,
  isMe = false,
}: NestedPlayer & { isMe?: boolean }) => (
  <Link href={`/players/${id}`} className="padding block hover:bg-gray-100">
    <li className="flex justify-between gap-x-6 py-5">
      <div className="flex min-w-0 gap-x-4">
        <div className="min-w-0 flex-auto">
          <div className="flex items-center gap-x-1.5">
            {title ? (
              <div className="bg-chessdrop-600 rounded-lg px-1.5 py-0.5 text-xs font-medium text-white">
                {title}
              </div>
            ) : null}
            <p className="truncate text-base leading-6 font-semibold text-gray-900">
              {name}
            </p>
            {is_chessdrop_user ? (
              <Tooltip text="ChessDrop member">
                <UserIcon className="text-chessdrop-600 size-4 shrink-0" />
              </Tooltip>
            ) : null}
            {isMe ? (
              <span className="rounded-md bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-500 ring-1 ring-gray-200 ring-inset">
                You
              </span>
            ) : null}
          </div>
          <div className="mt-1 flex h-5 items-center gap-x-1">
            {elo ? <p className="text-sm text-gray-600">{elo}</p> : null}
          </div>
        </div>
      </div>
      {!is_chessdrop_user ? (
        <div className="hidden shrink-0 sm:flex sm:flex-col sm:items-end">
          <p className="text-sm text-gray-600">
            {games_count} game{games_count === 1 ? "" : "s"}
          </p>
        </div>
      ) : null}
    </li>
  </Link>
)
